<!-- Wildcard certificate confirmation V1.4; 2026-10-30 00:11:36 CST; parent 518cf6c2934e93d3ca74cdcdc197c61ae7d7f390. -->
<!-- Wildcard acceleration V1.3; 2026-10-30 00:01:53 CST; parent 5833266438498f8cf299ba3a2688ef5ffaf86149. -->
<!-- Multi access domains V1.0; 2026-10-29 00:15:41 CST; parent 66595a52589c679584d5d9819a68d104859419b9. -->
<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- Console V0.1.131; 2026-10-02 23:21:53 CST; parent c017cdf8a4441fc43102fdd82ff0eb8c24b7b1f0; require node acknowledgement before success. -->
<!-- Console V0.1.130; 2026-10-02 20:51:43 CST; parent 5abb11ed04c30c2573a4557e316fd48d656507ca; CDN-RELIABILITY-20261002. -->
<script setup lang="ts">
/* HotX 域名完整配置弹窗
 * 创建时间：2026-09-04 CST
 * 本组件编排现有域名、DNS 接入记录、源站、缓存和单域名发布接口，让“配置”信息完整且可复制。
 * 证书申请、泛域名证书、套餐扣费保持独立，不在这里复制或改写对应业务流程。
 */
import { computed, ref, watch } from 'vue';
import DomainAccessTargets from './DomainAccessTargets.vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { consoleApi } from '../api/client';
import type { CacheProfile, Certificate, Domain, Origin, OriginProtocol } from '../api/types';
import { setConsoleNavigationDirty } from '../router/navigationState';
import { publishCurrentDomainConfig } from '../utils/autoPublish';
import { buildOriginCreatePayload } from '../views/originPayload';
import { validateOriginCompatibility } from '../views/originCompatibility';

interface OriginDraft {
  id?: number;
  originType: string;
  originAddress: string;
  originPort: number;
  host: string;
  tlsSni: string;
  protocol: OriginProtocol;
  originPath: string;
  weight: number;
  priority: number;
  connectTimeoutMs: number;
  readTimeoutMs: number;
  sendTimeoutMs: number;
}

const props = defineProps<{ modelValue: boolean; domain?: Domain }>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  saved: [message: string];
}>();

const loading = ref(false);
const saving = ref(false);
const error = ref('');
const domainName = ref('');
const serviceEnabled = ref(true);
const origins = ref<OriginDraft[]>([]);
const removedOriginIds = ref<number[]>([]);
const cacheProfile = ref<CacheProfile | null>(null);
const baseline = ref('');
const existingCertificates = ref<Certificate[]>([]);
const selectedCertificateId = ref('');
const bindingCertificate = ref(false);
const certificateFeedback = ref('');
let certificateOperation = 0;
const wildcardConfiguration = computed(() => props.domain?.domain.startsWith('*.') === true);
const usableWildcardCertificates = computed(() => existingCertificates.value.filter(c =>
  ['ISSUED','VALID','ACTIVE','BOUND'].includes(c.status.toUpperCase())
  && c.sans?.some(san => san.toLowerCase() === props.domain?.domain.toLowerCase())
  && (!c.notAfter || Date.parse(c.notAfter) > Date.now())
));
async function loadCertificateOptions(current: () => boolean) {
  if (!wildcardConfiguration.value) return;
  try { const rows = await consoleApi.certificates.list(); if (current()) existingCertificates.value = rows; }
  catch { if (current()) certificateFeedback.value = '已有证书读取失败，请关闭后重新打开配置；不影响当前 HTTP。'; }
}
async function bindExistingCertificate() {
  const domain = props.domain;
  const selected = usableWildcardCertificates.value.find(c => c.id === Number(selectedCertificateId.value));
  if (!domain || !wildcardConfiguration.value || !selected || bindingCertificate.value || dirty.value) return;
  const operation = ++certificateOperation;
  const current = () => operation === certificateOperation && props.modelValue && props.domain?.id === domain.id;
  bindingCertificate.value = true;
  certificateFeedback.value = '正在绑定当前泛域名证书…';
  try {
    await ElMessageBox.confirm(`将证书「${selected.certificateName}」绑定到 ${domain.domain}，仅影响当前域名的 HTTPS；节点部署后才会生效。`, '确认绑定证书', {confirmButtonText: '确认绑定', cancelButtonText: '取消', type: 'warning'});
    if (!current()) return;
    await consoleApi.certificates.bind(selected.id, domain.id, {acknowledged: true, actionLabel: '绑定已有泛域名证书', reason: '用户在当前域名配置中二次确认绑定已有证书', impactPreview: [`仅绑定当前域名 ${domain.domain}（ID ${domain.id}）`, '需等待节点部署，HTTP 回源和其他域名保持不变']});
    if (!current()) return;
    certificateFeedback.value = '已绑定，HTTPS 以节点部署回执为准；无需重新签发证书。';
    selectedCertificateId.value = '';
    const certificates = await consoleApi.certificates.list();
    if (!current()) return;
    existingCertificates.value = certificates;
    emit('saved', certificateFeedback.value);
  } catch (e) { if (current()) certificateFeedback.value = e === 'cancel' || e === 'close' ? '已取消绑定，当前配置未变更。' : e instanceof Error ? e.message : '绑定失败，请稍后重试。'; }
  finally { if (operation === certificateOperation) bindingCertificate.value = false; }
}
const accessCopyCountdown = ref(0);
let accessCopyTimer: ReturnType<typeof setInterval> | undefined;

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
});
const normalizedDomain = computed(() => domainName.value.trim().toLowerCase());
const accessRecordValue = computed(() => {
  const original = props.domain?.domain?.toLowerCase();
  if (normalizedDomain.value && normalizedDomain.value !== original) {
    return '保存后由服务端确认新域名接入地址';
  }
  const values = props.domain?.expectedValues?.filter(Boolean) || [];
  return values.join(' / ') || props.domain?.cnameTarget || (normalizedDomain.value
    ? '等待服务端确认接入记录'
    : '等待生成接入记录');
});
const snapshot = computed(() => JSON.stringify({
  domain: normalizedDomain.value,
  serviceEnabled: serviceEnabled.value,
  origins: origins.value,
  removedOriginIds: removedOriginIds.value,
  cache: cacheProfile.value
}));
const dirty = computed(() => Boolean(baseline.value && snapshot.value !== baseline.value));

watch(() => [props.modelValue, props.domain?.id] as const, async ([value]) => {
  reset();
  if (value && props.domain) await loadConfiguration(props.domain);
});
watch(snapshot, () => setConsoleNavigationDirty(props.modelValue && dirty.value));

function originDraft(origin?: Origin, domain = ''): OriginDraft {
  const protocol = origin?.protocol === 'HTTP' || origin?.protocol === 'HTTP_HTTPS' ? origin.protocol : 'HTTPS';
  return {
    id: origin?.id,
    originType: origin?.originType || 'PRIMARY',
    originAddress: origin?.originAddress || '',
    originPort: origin?.originPort || 443,
    host: origin?.host || domain,
    tlsSni: origin?.tlsSni || domain,
    protocol,
    originPath: origin?.originPath || '/',
    weight: origin?.weight ?? 100,
    priority: origin?.priority ?? 1,
    connectTimeoutMs: origin?.connectTimeoutMs ?? 3000,
    readTimeoutMs: origin?.readTimeoutMs ?? 30000,
    sendTimeoutMs: origin?.sendTimeoutMs ?? 3000
  };
}

async function loadConfiguration(domain: Domain) {
  const operation = certificateOperation;
  const current = () => operation === certificateOperation && props.modelValue && props.domain?.id === domain.id;
  loading.value = true;
  error.value = '';
  domainName.value = domain.domain;
  serviceEnabled.value = domain.serviceEnabled !== false && !['PAUSED', 'SUSPENDED'].includes(domain.status);
  removedOriginIds.value = [];
  try {
    const [originRows, profile] = await Promise.all([
      consoleApi.origins.list(domain.id),
      consoleApi.cache.profile(domain.id).catch(() => null)
    ]);
    if (!current()) return;
    origins.value = originRows.length
      ? originRows.map(row => originDraft(row, domain.domain))
      : [originDraft(undefined, domain.domain)];
    cacheProfile.value = profile;
    await loadCertificateOptions(current);
    if (!current()) return;
    baseline.value = snapshot.value;
    setConsoleNavigationDirty(false);
  } catch (e) {
    if (current()) error.value = e instanceof Error ? e.message : '配置加载失败，请稍后重试。';
  } finally {
    if (current()) loading.value = false;
  }
}

function reset() {
  certificateOperation++;
  loading.value = false;
  bindingCertificate.value = false;
  error.value = '';
  domainName.value = '';
  origins.value = [];
  removedOriginIds.value = [];
  cacheProfile.value = null;
  baseline.value = '';
  existingCertificates.value = []; selectedCertificateId.value = ''; certificateFeedback.value = '';
  accessCopyCountdown.value = 0;
  if (accessCopyTimer) clearInterval(accessCopyTimer);
  accessCopyTimer = undefined;
  setConsoleNavigationDirty(false);
}

/** 配置弹窗复制不能依赖单一 Clipboard API，权限受限时自动使用兼容方案。 */
async function copyAccessRecord() {
  if (!accessRecordValue.value || accessRecordValue.value === '等待生成接入记录' || accessCopyCountdown.value > 0) return;
  try {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(accessRecordValue.value);
      } catch {
        copyAccessRecordFallback(accessRecordValue.value);
      }
    } else {
      copyAccessRecordFallback(accessRecordValue.value);
    }
    ElMessage.success('接入记录已复制。');
    accessCopyCountdown.value = 5;
    if (accessCopyTimer) clearInterval(accessCopyTimer);
    accessCopyTimer = setInterval(() => {
      accessCopyCountdown.value -= 1;
      if (accessCopyCountdown.value <= 0 && accessCopyTimer) {
        clearInterval(accessCopyTimer);
        accessCopyTimer = undefined;
      }
    }, 1000);
  } catch {
    error.value = '复制失败，请手动选择接入域名后复制。';
    ElMessage.error(error.value);
  }
}

function copyAccessRecordFallback(value: string) {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('copy failed');
}

function addOrigin() {
  const domain = normalizedDomain.value || props.domain?.domain || '';
  origins.value.push({
    ...originDraft(undefined, domain),
    originType: origins.value.length ? 'BACKUP' : 'PRIMARY',
    priority: origins.value.length ? (origins.value.length + 1) * 10 : 1
  });
}

function removeOrigin(index: number) {
  if (origins.value.length <= 1) {
    error.value = 'CDN 加速至少需要保留一个源站。';
    return;
  }
  const [removed] = origins.value.splice(index, 1);
  if (removed?.id) removedOriginIds.value.push(removed.id);
}

async function requestClose(done?: () => void) {
  if (bindingCertificate.value) return;
  if (dirty.value) {
    try {
      await ElMessageBox.confirm('当前配置尚未保存，确定关闭吗？', '放弃修改', {
        confirmButtonText: '放弃修改',
        cancelButtonText: '继续编辑',
        type: 'warning'
      });
    } catch {
      return;
    }
  }
  done?.();
  visible.value = false;
}

function validDomain(value: string) {
  return value.length <= 253 && /^(?:\*\.)?(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(value);
}

async function save() {
  const domain = props.domain;
  if (!domain || saving.value || bindingCertificate.value) return;
  error.value = '';
  if (!validDomain(normalizedDomain.value)) {
    error.value = '请输入正确的加速域名。';
    return;
  }
  if (!origins.value.length || origins.value.some(row => !row.originAddress.trim())) {
    error.value = '请为每个源站填写真实地址。';
    return;
  }

  saving.value = true;
  try {
    const prepared = origins.value.map(row => buildOriginCreatePayload(row, domain.id, normalizedDomain.value));
    validateOriginCompatibility(prepared[0], prepared.slice(1).map((row, id) => ({ ...row, id })));
    const oldDomain = domain.domain.toLowerCase();
    const renamed = normalizedDomain.value !== oldDomain;
    if (renamed) {
      await ElMessageBox.confirm(
        `域名将从 ${domain.domain} 变更为 ${normalizedDomain.value}。系统会重新生成 DNS 接入记录，原域名立即停止使用，证书需要按新域名重新匹配。`,
        '确认变更域名',
        { confirmButtonText: '确认变更并重新接入', cancelButtonText: '取消', type: 'warning' }
      );
      await consoleApi.domains.update(domain.id, normalizedDomain.value);
    }

    for (const row of origins.value) {
      const draft = {
        ...row,
        host: renamed && row.host.toLowerCase() === oldDomain ? normalizedDomain.value : row.host,
        tlsSni: renamed && row.tlsSni.toLowerCase() === oldDomain ? normalizedDomain.value : row.tlsSni
      };
      const payload = buildOriginCreatePayload(draft, domain.id, normalizedDomain.value);
      if (row.id) {
        // 源站修改参数边界修复，更新时间：2026-09-04 CST。
        // UpdateOriginRequest 不接收仅新增时使用的 domainId/originType，避免严格 JSON 校验拒绝保存。
        const { domainId: _domainId, originType: _originType, ...updatePayload } = payload;
        await consoleApi.origins.update(row.id, updatePayload);
      } else {
        await consoleApi.origins.create(payload);
      }
    }
    for (const id of removedOriginIds.value) await consoleApi.origins.delete(id);
    if (cacheProfile.value) {
      // 2026-09-04 22:00 CST: CacheProfile 是查询响应，后端更新接口只接受下面 7 个设置字段。
      // 严禁回传 id、状态、版本号和规则等只读字段，避免严格 JSON 校验拒绝整个保存流程。
      const cacheSettings = {
        cacheEnabled: cacheProfile.value.cacheEnabled,
        defaultTtlSeconds: cacheProfile.value.defaultTtlSeconds,
        browserTtlSeconds: cacheProfile.value.browserTtlSeconds,
        respectOriginCacheControl: cacheProfile.value.respectOriginCacheControl,
        queryCacheEnabled: cacheProfile.value.queryCacheEnabled,
        ignoreQueryOrder: cacheProfile.value.ignoreQueryOrder,
        staleIfErrorEnabled: cacheProfile.value.staleIfErrorEnabled
      } as CacheProfile;
      await consoleApi.cache.updateProfile(domain.id, cacheSettings);
    }

    const wasEnabled = domain.serviceEnabled !== false && !['PAUSED', 'SUSPENDED'].includes(domain.status);
    if (serviceEnabled.value !== wasEnabled) {
      if (serviceEnabled.value) await consoleApi.domains.enable(domain.id);
      else await consoleApi.domains.pause(domain.id);
    }

    const release = await publishCurrentDomainConfig(
      domain.id,
      `客户保存 ${normalizedDomain.value} 完整域名配置后自动发布当前域名配置`,
      true
    );
    if (!release?.ok) {
      // Saved origin IDs may have changed. Reload before retry to avoid duplicate creates/deletes.
      await loadConfiguration(domain);
      error.value = release?.message || '配置已保存，但未确认节点发布，请重试。';
      return;
    }
    baseline.value = snapshot.value;
    setConsoleNavigationDirty(false);
    emit('saved', [renamed ? '域名已变更，请重新配置 DNS。' : '域名配置已保存。', release?.message].filter(Boolean).join(' '));
    visible.value = false;
  } catch (e) {
    if (e !== 'cancel' && e !== 'close') error.value = e instanceof Error ? e.message : '配置保存失败，请稍后重试。';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <el-dialog v-model="visible" title="编辑域名配置" width="min(920px, 94vw)" class="domain-config-dialog" destroy-on-close :close-on-click-modal="false" :before-close="requestClose" :show-close="!bindingCertificate" :close-on-press-escape="!bindingCertificate">
    <div v-loading="loading || bindingCertificate" class="domain-config-body">
      <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" />

      <section class="config-section">
        <header><div><strong>基础设置</strong><small>域名变更后需要重新配置 DNS</small></div><el-switch v-model="serviceEnabled" active-text="启用加速" inactive-text="暂停加速" /></header>
        <el-form label-position="top">
          <el-form-item label="加速域名"><el-input v-model="domainName" size="large" placeholder="www.example.com" /><p class="field-help">保存前会二次确认，并按新域名重新生成接入记录。</p></el-form-item>
        </el-form>
        <DomainAccessTargets v-if="props.domain && props.domain.recordType !== 'A' && normalizedDomain === props.domain.domain.toLowerCase()" :domain-id="props.domain.id" :domain="props.domain.domain" />
        <div v-else class="access-record-card">
          <div><small>记录类型</small><strong>{{ props.domain?.recordType || 'CNAME' }}</strong></div>
          <div class="access-record-value"><small>接入域名 / 记录值</small><code>{{ accessRecordValue }}</code></div>
          <el-button type="primary" plain :disabled="accessCopyCountdown > 0 || accessRecordValue === '等待生成接入记录'" @click="copyAccessRecord">
            {{ accessCopyCountdown > 0 ? `已复制 ${accessCopyCountdown} 秒` : '复制接入域名' }}
          </el-button>
        </div>
      </section>

      <section class="config-section">
        <header><div><strong>源站配置</strong><small>编辑当前源站，也可以添加备用源站</small></div><el-button type="primary" plain @click="addOrigin">添加源站</el-button></header>
        <article v-for="(origin, index) in origins" :key="origin.id || `new-${index}`" class="origin-editor">
          <header>
            <strong>{{ index === 0 ? '主源站' : `备用源站 ${index}` }}</strong>
            <!-- 主源站必须保留，更新时间：2026-09-04 CST。主源站可直接改全部参数，仅备用源站允许移除。 -->
            <el-button v-if="index > 0" type="danger" plain size="small" @click="removeOrigin(index)">移除</el-button>
          </header>
          <div class="form-grid">
            <el-form-item label="源站地址"><el-input v-model="origin.originAddress" placeholder="IP 或源站域名" /></el-form-item>
            <el-form-item label="端口"><el-input-number v-model="origin.originPort" :min="1" :max="65535" /></el-form-item>
            <el-form-item label="回源协议"><el-select v-model="origin.protocol"><el-option label="HTTP" value="HTTP" /><el-option label="HTTPS" value="HTTPS" /><el-option label="HTTP + HTTPS" value="HTTP_HTTPS" /></el-select></el-form-item>
            <el-form-item label="回源 Host"><el-input v-model="origin.host" placeholder="默认使用加速域名" /></el-form-item>
            <el-form-item label="TLS SNI"><el-input v-model="origin.tlsSni" placeholder="HTTPS 默认使用回源 Host" /></el-form-item>
            <el-form-item label="回源路径"><el-input v-model="origin.originPath" placeholder="/" /></el-form-item>
            <el-form-item label="权重"><el-input-number v-model="origin.weight" :min="1" :max="100" /></el-form-item>
            <el-form-item label="优先级"><el-input-number v-model="origin.priority" :min="1" :max="9999" /></el-form-item>
            <el-form-item label="连接超时(ms)"><el-input-number v-model="origin.connectTimeoutMs" :min="100" /></el-form-item>
            <el-form-item label="读取超时(ms)"><el-input-number v-model="origin.readTimeoutMs" :min="100" /></el-form-item>
            <el-form-item label="发送超时(ms)"><el-input-number v-model="origin.sendTimeoutMs" :min="100" /></el-form-item>
          </div>
        </article>
      </section>

      <section v-if="cacheProfile" class="config-section">
        <header><div><strong>缓存设置</strong><small>保存后与本域名配置一起发布</small></div><el-switch v-model="cacheProfile.cacheEnabled" active-text="启用缓存" inactive-text="关闭缓存" /></header>
        <div class="form-grid form-grid--two">
          <el-form-item label="默认 TTL（秒）"><el-input-number v-model="cacheProfile.defaultTtlSeconds" :min="0" /></el-form-item>
          <el-form-item label="浏览器 TTL（秒）"><el-input-number v-model="cacheProfile.browserTtlSeconds" :min="0" /></el-form-item>
          <el-form-item label="遵循源站 Cache-Control"><el-switch v-model="cacheProfile.respectOriginCacheControl" /></el-form-item>
          <el-form-item label="Query 缓存"><el-switch v-model="cacheProfile.queryCacheEnabled" /></el-form-item>
          <el-form-item label="忽略 Query 顺序"><el-switch v-model="cacheProfile.ignoreQueryOrder" /></el-form-item>
          <el-form-item label="源站异常使用过期缓存"><el-switch v-model="cacheProfile.staleIfErrorEnabled" /></el-form-item>
        </div>
      </section>

      <section v-if="wildcardConfiguration" class="config-section">
        <strong>泛域名 HTTPS · 使用已有证书</strong>
        <p>只列出覆盖当前泛域名的有效证书；绑定后仍需等待节点部署与 HTTPS 检查，不会修改源站或重新签发。</p>
        <label>已签发证书
          <select v-model="selectedCertificateId" aria-label="泛域名已签发证书" :disabled="loading || bindingCertificate">
            <option value="">请选择已有证书</option>
            <option v-for="certificate in usableWildcardCertificates" :key="certificate.id" :value="String(certificate.id)" :disabled="certificate.domainIds?.includes(domain?.id || 0)">{{ certificate.certificateName }}{{ certificate.domainIds?.includes(domain?.id || 0) ? '（已绑定）' : '' }}</option>
          </select>
        </label>
        <el-button :loading="bindingCertificate" :disabled="loading || saving || bindingCertificate || !selectedCertificateId || dirty" @click="bindExistingCertificate">绑定已有证书</el-button>
        <p v-if="certificateFeedback" role="status">{{ certificateFeedback }}</p>
      </section>
      <section class="config-section config-boundary">
        <strong>{{ wildcardConfiguration ? '套餐与证书申请保持原配置' : '套餐与证书保持原配置' }}</strong>
        <p>套餐通过“升级套餐”管理；证书继续使用独立证书管理流程，本弹窗不会改动泛域名证书申请。</p>
      </section>
    </div>
    <template #footer><el-button :disabled="bindingCertificate" @click="requestClose()">取消</el-button><el-button type="primary" :loading="saving" :disabled="loading || bindingCertificate" @click="save">保存并发布</el-button></template>
  </el-dialog>
</template>

<style scoped>
.domain-config-body { display: grid; max-height: min(70vh, 760px); gap: 16px; overflow-y: auto; padding-right: 6px; }
.config-section { border: 1px solid rgba(18, 63, 42, 0.14); border-radius: 18px; padding: 18px; background: linear-gradient(135deg, #fbfdf9, #f5f9f2); }
.config-section > header, .origin-editor > header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.config-section > header > div { display: grid; gap: 3px; }
.config-section header strong { color: #123f2a; font-size: 16px; }
.config-section header small, .config-boundary p { color: #6c7b72; }
.origin-editor { border: 1px solid rgba(18, 63, 42, 0.11); border-radius: 14px; padding: 14px; background: #fff; }
.origin-editor + .origin-editor { margin-top: 12px; }
.form-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 2px 14px; }
.form-grid--two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.form-grid :deep(.el-select), .form-grid :deep(.el-input-number) { width: 100%; }
.config-boundary p { margin: 8px 0 0; line-height: 1.65; }
.field-help { width: 100%; margin: 7px 0 0; color: #6c7b72; }
.access-record-card { display:grid;grid-template-columns:110px minmax(0,1fr) auto;align-items:center;gap:14px;margin-top:4px;padding:14px;border:1px solid rgba(18,63,42,.12);border-radius:14px;background:#fff; }
.access-record-card > div { display:grid;gap:5px;min-width:0; }.access-record-card small{color:#6c7b72}.access-record-card code{overflow:auto;color:#125738;font-weight:800;white-space:nowrap}
@media (max-width: 760px) { .form-grid, .form-grid--two { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .access-record-card { grid-template-columns:1fr;align-items:stretch; } }
</style>
